import { BookOpen, CheckCircle2 } from 'lucide-react'

// Staff-facing how-to-use-the-app walkthrough, written for a non-technical
// marketing manager. Keep it task-focused: click here, fill in that, look here.
export default function SetupGuide() {
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-brand" />
          Setup Guide
        </h3>
        <p className="text-sm text-gray-500">
          Everything you need to do to bring a new client onboard and start taking orders from them. Work through it in order — each step takes a few minutes.
        </p>
      </div>

      <Step number={0} title="Adding more WG staff to the app">
        <p>If a colleague at WG needs to use the app:</p>
        <ol>
          <li>Go to <Link path="/admin/team" />.</li>
          <li>Type their full name and work email in the <strong>Invite someone</strong> form.</li>
          <li>Choose role <strong>Staff (admin)</strong>.</li>
          <li>Click <strong>Send invite</strong>. They get an email with a link to set a password. Once they click it, they can sign in and see the full admin app.</li>
        </ol>
        <p>Use the <strong>Client</strong> role only if you're adding a single client user manually. For bulk client invites, use <Link path="/admin/import-clients" /> instead.</p>
      </Step>

      <Step number={1} title="Set up your own brand">
        <ol>
          <li>Go to <Link path="/settings" /> in the sidebar.</li>
          <li>Make sure the dropdown at the top of the page is on <strong>[Global] Walter Geering</strong>.</li>
          <li>Upload your logo, set your brand colours, and write the email footer you want at the bottom of every order email.</li>
          <li>Click <strong>Save profile</strong>.</li>
        </ol>
        <p>This is what staff see when they're logged in, and what gets used as the default when a client doesn't have their own brand yet.</p>
      </Step>

      <Step number={2} title="Configure email delivery">
        <ol>
          <li>Still on <Link path="/settings" />, scroll down to <strong>Email delivery</strong>.</li>
          <li><strong>From address</strong> — what shows in the sender field of the order email (e.g. <em>WG Orders &lt;orders@waltergeering.co.uk&gt;</em>).</li>
          <li><strong>Reply-to</strong> — where customer replies go (often the same as From, but you can use a real inbox here if you want replies separated).</li>
          <li><strong>BCC</strong> (optional) — every outbound order email is also sent to this address. Useful as an archive.</li>
          <li><strong>Internal recipients</strong> — the comma-separated list of WG people who get the "new order has been placed" notification.</li>
          <li>Save.</li>
        </ol>
        <p>Tip: while you're getting set up, scroll down to <strong>Test mode</strong>, fill in a test email and turn the toggle ON. Every order email will be diverted to that test address until you turn it off — handy for trial runs.</p>
      </Step>

      <Step number={3} title="Create the client group">
        <p>A "client group" is the parent organisation (e.g. Away Resorts). It holds all the individual sites underneath it.</p>
        <ol>
          <li>Go to <Link path="/admin/client-groups" />.</li>
          <li>Click <strong>+ New Group</strong>, name it (e.g. <em>Away Resorts</em>), and Create.</li>
          <li>Click into the new group → fill in their main contact name, contact email, billing address, internal notes.</li>
          <li>Save.</li>
        </ol>
      </Step>

      <Step number={4} title="Set up the client's brand">
        <p>So when their users log in they see their colours and logo, not yours.</p>
        <ol>
          <li>Go to <Link path="/settings" />.</li>
          <li>Click <strong>+ New profile</strong>, name it the same as the client group (e.g. <em>Away Resorts</em>).</li>
          <li>Upload their logo, set their brand colours, set their email footer.</li>
          <li>(Optional but recommended) Edit the <strong>Client help &amp; instructions</strong> text so it's worded for their users.</li>
          <li>Save profile.</li>
          <li>Now go back to <Link path="/admin/client-groups" />, click into the group, change <strong>Brand profile</strong> to the one you just created, Save.</li>
        </ol>
        <p>Every site under that group will inherit those colours and the logo from now on.</p>
      </Step>

      <Step number={5} title="Add the client's sites">
        <p>Each "site" is a location they order to (a holiday park, a branch, a hotel — whatever's relevant).</p>
        <ol>
          <li>Go to <Link path="/companies" />.</li>
          <li>Click <strong>+ New Company</strong>, pick the client group from the dropdown, give it the site name (e.g. <em>Bude Holiday Resort</em>), Create.</li>
          <li>Click into the new company → fill in the delivery address, postcode, primary contact, etc.</li>
          <li>Save.</li>
          <li>Repeat for every site.</li>
        </ol>
        <p>If the client has given you a list of sites in a spreadsheet, ask the developer to bulk-import them — saves you typing.</p>
      </Step>

      <Step number={6} title="Add products to the catalogue">
        <ol>
          <li>Go to <Link path="/products" />.</li>
          <li>Click <strong>+ New Product</strong>. You only need a SKU + name to start — the rest opens automatically on the next screen.</li>
          <li>On the product detail page, fill in:
            <ul>
              <li><strong>Image</strong> — upload a clear photo. Square-ish images look best on the catalogue cards.</li>
              <li><strong>Description</strong> — what the client sees when they click into the product.</li>
              <li><strong>Unit</strong> — the word that follows the quantity in emails (<em>box</em>, <em>each</em>, <em>case</em>).</li>
              <li><strong>Pack size</strong> — how many items in one box/case.</li>
              <li><strong>Price</strong> — the headline price you want shown.</li>
            </ul>
          </li>
          <li>Save.</li>
          <li>Repeat for every product. Each SKU has to be unique.</li>
        </ol>
      </Step>

      <Step number={7} title="Decide which products each site can order">
        <ol>
          <li>Go to <Link path="/admin/product-visibility" />.</li>
          <li>You'll see a grid: products down the left, sites across the top.</li>
          <li>Tick the box where a product should be available to a site.</li>
          <li>Use the filter dropdown to narrow down to one client group at a time.</li>
          <li>Per-row <strong>all</strong> / <strong>none</strong> buttons on the right quickly turn a product on or off for every visible site.</li>
        </ol>
        <p>Only ticked products appear in the client's "Place Order" page and catalogue. Untick to hide.</p>
      </Step>

      <Step number={8} title="Preview what the client will see">
        <ol>
          <li>Go to <Link path="/companies" />.</li>
          <li>Find any of the new client's sites in the list.</li>
          <li>Click <strong>Preview as client</strong> next to that site.</li>
          <li>A new tab opens showing the client portal exactly as their users will see it: their colours, their logo, their visible products, their order page, their Help text.</li>
          <li>Submit buttons are disabled in preview, so you can't accidentally place a real order.</li>
          <li>Spot-check everything looks right before you invite users. Close the tab when you're done.</li>
        </ol>
      </Step>

      <Step number={9} title="Send a test order">
        <ol>
          <li>Make sure <strong>Test mode</strong> is ON in <Link path="/settings" /> with your own email set as the test address.</li>
          <li>Go to <Link path="/orders" /> → <strong>+ New Order</strong>.</li>
          <li>Pick one of the client's sites, fill in a Customer PO, add a consignment (Hamper for / No.), add a couple of products, Save.</li>
          <li>Click <strong>Send test</strong>. The email lands in your inbox with <strong>[TEST]</strong> at the front of the subject — that's how you know it didn't go to a real client.</li>
          <li>Open the test email. Check the layout, the colours, the product totals, the recipient details. Tweak in <Link path="/settings" /> → <strong>Email templates</strong> if anything's off.</li>
        </ol>
      </Step>

      <Step number={10} title="Invite the client's users">
        <ol>
          <li>Get an Excel file from the client with three columns: <strong>Name</strong>, <strong>Email</strong>, <strong>Location</strong>.</li>
          <li>Go to <Link path="/admin/import-clients" />.</li>
          <li>Click <strong>Upload XLSX</strong> and pick the file.</li>
          <li>The page lists every row with the company it's been auto-matched to. People at "Central Team" or similar will be marked to receive access to every site — tick the list on the right to confirm which sites they should reach.</li>
          <li>Untick anyone you don't want invited just yet.</li>
          <li>Click <strong>Send invites</strong>. Every ticked person gets an email with a link to set their password and log in.</li>
        </ol>
        <p>Tip: if the client has dozens of users, ask the developer to set up custom SMTP before doing a big batch — the default sender has a per-hour rate limit.</p>
      </Step>

      <Step number={11} title="Go live">
        <ol>
          <li>Turn <strong>Test mode</strong> OFF in <Link path="/settings" />.</li>
          <li>From now on, when clients submit orders, you'll receive an automatic notification email at the Internal recipients address.</li>
          <li>You'll see all incoming orders in <Link path="/orders" /> in date order.</li>
        </ol>
      </Step>

      <Step number={12} title="Day-to-day: managing orders">
        <ol>
          <li>Orders the client submits arrive in <Link path="/orders" /> with status <em>sent</em>.</li>
          <li>Click an order to see its details, consignments, and product breakdown.</li>
          <li>You can edit, add or remove products and consignments — useful for fixing typos, splitting deliveries, etc.</li>
          <li>The <strong>View as client</strong> button shows you the exact order page the client received in their email.</li>
          <li>Use <strong>Send</strong> again if you want to re-send a confirmation after edits.</li>
        </ol>
      </Step>

      <Step number={13} title="Day-to-day: reports">
        <p>Useful at month-end or when the client asks "how much have we ordered this quarter".</p>
        <ol>
          <li>Go to <Link path="/admin/reports" />.</li>
          <li>Pick the client group from the dropdown.</li>
          <li>Set a date range (optional).</li>
          <li>You'll see totals: number of orders, total value, breakdown per site.</li>
          <li>Click any site row to expand and see exactly which products they ordered and how many of each.</li>
          <li>Click <strong>Download CSV</strong> to export it for Excel.</li>
        </ol>
      </Step>
    </div>
  )
}

function Step({ number, title, children }) {
  return (
    <div className="card">
      <div className="flex items-start gap-3 mb-2">
        <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-semibold shrink-0">
          {number}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-800">{title}</h4>
        </div>
      </div>
      <div className="ml-11 prose prose-sm max-w-none [&_ol]:my-2 [&_ul]:my-2 [&_li]:my-1 [&_a]:text-brand-dark [&_a]:underline">
        {children}
      </div>
      <div className="ml-11 mt-2 flex items-center gap-1 text-xs text-gray-400">
        <CheckCircle2 className="w-3.5 h-3.5" /> Done? Move on to the next one.
      </div>
    </div>
  )
}

function Link({ path }) {
  // Show the human-readable section name rather than the URL path.
  const labels = {
    '/settings':                   'Settings',
    '/admin/client-groups':        'Client Groups',
    '/companies':                  'Companies',
    '/products':                   'Products',
    '/admin/product-visibility':   'Product Visibility',
    '/orders':                     'Orders',
    '/admin/reports':              'Reports',
    '/admin/import-clients':       'Import Clients',
  }
  return <a href={path} className="text-brand-dark underline font-medium">{labels[path] || path}</a>
}
